import { useState, useMemo, memo, useEffect } from 'react';
import {
  Table,
  TableHeader,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  Checkbox,
  Button,
  SearchBox,
  Dropdown,
  Option,
  Badge,
  Spinner,
  Text,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogContent,
  DialogBody,
  DialogActions,
  Field,
  Input,
  Textarea,
  useToastController,
  Toast,
  ToastTitle,
  ToastBody,
  Toaster,
} from '@fluentui/react-components';
import {
  Power20Regular,
  SignOut20Regular,
  Chat20Regular,
  ArrowClockwise20Regular,
  Delete20Regular,
  CheckmarkCircle20Regular,
} from '@fluentui/react-icons';
import { useAdminSessions, useAdminDisconnectSession, useAdminLogOffSession, useAdminSendMessage, useBulkSessionAction, useForceHealthCheck, useForceRefresh } from '@/hooks/useAdminQueries';
import { AdminProgressBar } from '@/components/AdminProgressBar';
import type { Session, SessionFilters, BulkSessionRequest, SessionState } from '@/types/api';
import { formatDateLocale } from '@/utils/dateFormatters';

/**
 * AdminSessionsTable component displays a comprehensive table of all AVD sessions
 * with advanced filtering, search, and bulk action capabilities for administrators.
 *
 * **Why use React.memo:**
 * This component is wrapped with React.memo to prevent unnecessary re-renders when
 * parent components update. Since this is a large, complex component that renders
 * potentially hundreds of session rows, memoization significantly improves performance
 * by ensuring the component only re-renders when its data or internal state changes.
 * The component has no props, so it will only re-render when React Query data updates.
 */
function AdminSessionsTableComponent() {
  const { data: sessions = [], isLoading, error, refetch } = useAdminSessions();
  const disconnectMutation = useAdminDisconnectSession();
  const logOffMutation = useAdminLogOffSession();
  const sendMessageMutation = useAdminSendMessage();
  const bulkActionMutation = useBulkSessionAction();
  const forceHealthCheckMutation = useForceHealthCheck();
  const forceRefreshMutation = useForceRefresh();
  const { dispatchToast } = useToastController();

  // State for filters and search
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState(''); // Immediate input value for controlled component
  const [filters, setFilters] = useState<SessionFilters>({});
  const [selectedSessions, setSelectedSessions] = useState<Set<string>>(new Set());

  // Debounce search input to prevent excessive filtering
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setSearchTerm(searchInput);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchInput]);

  // Dialog state
  const [showMessageDialog, setShowMessageDialog] = useState(false);
  const [showBulkMessageDialog, setShowBulkMessageDialog] = useState(false);
  const [messageTitle, setMessageTitle] = useState('');
  const [messageBody, setMessageBody] = useState('');
  const [targetSessionId, setTargetSessionId] = useState<string | null>(null);

  // Get unique values for filter dropdowns
  const uniqueHostPools = useMemo(() =>
    [...new Set(sessions.map(s => s.hostPoolName))].sort(),
    [sessions]
  );

  const uniqueSessionHosts = useMemo(() => {
    const filteredSessions = filters.hostPool
      ? sessions.filter(s => s.hostPoolName === filters.hostPool)
      : sessions;
    return [...new Set(filteredSessions.map(s => s.sessionHostName))].sort();
  }, [sessions, filters.hostPool]);

  // Filter and search sessions
  const filteredSessions = useMemo(() => {
    return sessions.filter(session => {
      // Search filter
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        const matchesSearch =
          session.userPrincipalName.toLowerCase().includes(searchLower) ||
          session.hostPoolName.toLowerCase().includes(searchLower) ||
          session.sessionHostName.toLowerCase().includes(searchLower);
        if (!matchesSearch) return false;
      }

      // Host pool filter
      if (filters.hostPool && session.hostPoolName !== filters.hostPool) {
        return false;
      }

      // Session host filter
      if (filters.sessionHost && session.sessionHostName !== filters.sessionHost) {
        return false;
      }

      // Session state filter
      if (filters.sessionState && session.sessionState !== filters.sessionState) {
        return false;
      }

      // Orphaned filter
      if (filters.orphanedOnly && !session.isOrphaned) {
        return false;
      }

      return true;
    });
  }, [sessions, searchTerm, filters]);

  // Selection handlers
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedSessions(new Set(filteredSessions.map(s => s.id)));
    } else {
      setSelectedSessions(new Set());
    }
  };

  const handleSelectSession = (sessionId: string, checked: boolean) => {
    const newSelected = new Set(selectedSessions);
    if (checked) {
      newSelected.add(sessionId);
    } else {
      newSelected.delete(sessionId);
    }
    setSelectedSessions(newSelected);
  };

  // Action handlers
  const handleSingleAction = async (sessionId: string, action: 'disconnect' | 'logoff' | 'message') => {
    const session = sessions.find(s => s.id === sessionId);
    if (!session) return;

    try {
      const actionRequest = {
        hostPoolName: session.hostPoolName,
        sessionHostName: session.sessionHostName,
      };

      switch (action) {
        case 'disconnect':
          await disconnectMutation.mutateAsync({ sessionId, request: actionRequest });
          dispatchToast(
            <Toast>
              <ToastTitle>Success</ToastTitle>
              <ToastBody>Session disconnected successfully</ToastBody>
            </Toast>,
            { intent: 'success' }
          );
          break;
        case 'logoff':
          await logOffMutation.mutateAsync({ sessionId, request: actionRequest });
          dispatchToast(
            <Toast>
              <ToastTitle>Success</ToastTitle>
              <ToastBody>Session logged off successfully</ToastBody>
            </Toast>,
            { intent: 'success' }
          );
          break;
        case 'message':
          setTargetSessionId(sessionId);
          setShowMessageDialog(true);
          break;
      }
    } catch (error) {
      dispatchToast(
        <Toast>
          <ToastTitle>Error</ToastTitle>
          <ToastBody>Failed to perform action: {(error as Error).message}</ToastBody>
        </Toast>,
        { intent: 'error' }
      );
    }
  };

  const handleSendMessage = async () => {
    if (!targetSessionId || !messageTitle.trim() || !messageBody.trim()) return;

    const session = sessions.find(s => s.id === targetSessionId);
    if (!session) return;

    try {
      await sendMessageMutation.mutateAsync({
        sessionId: targetSessionId,
        request: {
          hostPoolName: session.hostPoolName,
          sessionHostName: session.sessionHostName,
          messageTitle: messageTitle.trim(),
          messageBody: messageBody.trim(),
        },
      });

      dispatchToast(
        <Toast>
          <ToastTitle>Success</ToastTitle>
          <ToastBody>Message sent successfully</ToastBody>
        </Toast>,
        { intent: 'success' }
      );

      setShowMessageDialog(false);
      setMessageTitle('');
      setMessageBody('');
      setTargetSessionId(null);
    } catch (error) {
      dispatchToast(
        <Toast>
          <ToastTitle>Error</ToastTitle>
          <ToastBody>Failed to send message: {(error as Error).message}</ToastBody>
        </Toast>,
        { intent: 'error' }
      );
    }
  };

  const handleBulkAction = async (action: 'disconnect' | 'logoff' | 'message') => {
    if (selectedSessions.size === 0) return;

    const allSelectedSessions = Array.from(selectedSessions)
      .map(sessionId => sessions.find(s => s.id === sessionId))
      .filter((session): session is Session => session !== null);

    // For logoff, allow Active, Disconnected, and UserDisconnected sessions
    // For disconnect and message, only allow Active sessions
    const eligibleSessions = action === 'logoff'
      ? allSelectedSessions.filter(session =>
          session.sessionState === 'Active' ||
          session.sessionState === 'Disconnected' ||
          session.sessionState === 'UserDisconnected')
      : allSelectedSessions.filter(session => session.sessionState === 'Active');

    const skippedCount = allSelectedSessions.length - eligibleSessions.length;

    if (skippedCount > 0) {
      const actionText = action === 'disconnect' ? 'disconnected' : action === 'logoff' ? 'logged off' : 'sent messages';
      const eligibilityText = action === 'logoff'
        ? 'Only Active or Disconnected sessions can be logged off'
        : 'Only active sessions can be ' + actionText;
      dispatchToast(
        <Toast>
          <ToastTitle>Warning</ToastTitle>
          <ToastBody>
            {skippedCount} session{skippedCount > 1 ? 's' : ''} will be skipped.
            {eligibilityText}.
          </ToastBody>
        </Toast>,
        { intent: 'warning' }
      );
    }

    const selectedSessionsData: BulkSessionRequest[] = eligibleSessions.map(session => ({
      sessionId: session.id,
      hostPoolName: session.hostPoolName,
      sessionHostName: session.sessionHostName,
    }));

    if (selectedSessionsData.length === 0) {
      dispatchToast(
        <Toast>
          <ToastTitle>No Eligible Sessions</ToastTitle>
          <ToastBody>No eligible sessions selected for this action.</ToastBody>
        </Toast>,
        { intent: 'warning' }
      );
      return;
    }

    if (action === 'message') {
      setShowBulkMessageDialog(true);
      return;
    }

    try {
      const result = await bulkActionMutation.mutateAsync({
        action,
        sessions: selectedSessionsData,
      });

      const successCount = result.results.filter(r => r.success).length;
      const totalCount = result.results.length;

      dispatchToast(
        <Toast>
          <ToastTitle>Bulk Action Complete</ToastTitle>
          <ToastBody>{successCount}/{totalCount} sessions processed successfully</ToastBody>
        </Toast>,
        { intent: successCount === totalCount ? 'success' : 'warning' }
      );

      setSelectedSessions(new Set());
    } catch (error) {
      dispatchToast(
        <Toast>
          <ToastTitle>Error</ToastTitle>
          <ToastBody>Failed to perform bulk action: {(error as Error).message}</ToastBody>
        </Toast>,
        { intent: 'error' }
      );
    }
  };

  const handleBulkSendMessage = async () => {
    if (!messageTitle.trim() || !messageBody.trim()) return;

    // Filter to only include active sessions
    const allSelectedSessions = Array.from(selectedSessions)
      .map(sessionId => sessions.find(s => s.id === sessionId))
      .filter((session): session is Session => session !== null);

    const activeSelectedSessions = allSelectedSessions.filter(session => session.sessionState === 'Active');
    const selectedSessionsData: BulkSessionRequest[] = activeSelectedSessions.map(session => ({
      sessionId: session.id,
      hostPoolName: session.hostPoolName,
      sessionHostName: session.sessionHostName,
    }));

    try {
      const result = await bulkActionMutation.mutateAsync({
        action: 'message',
        sessions: selectedSessionsData,
        messageTitle: messageTitle.trim(),
        messageBody: messageBody.trim(),
      });

      const successCount = result.results.filter(r => r.success).length;
      const totalCount = result.results.length;

      dispatchToast(
        <Toast>
          <ToastTitle>Bulk Message Complete</ToastTitle>
          <ToastBody>Message sent to {successCount}/{totalCount} sessions successfully</ToastBody>
        </Toast>,
        { intent: successCount === totalCount ? 'success' : 'warning' }
      );

      setShowBulkMessageDialog(false);
      setMessageTitle('');
      setMessageBody('');
      setSelectedSessions(new Set());
    } catch (error) {
      dispatchToast(
        <Toast>
          <ToastTitle>Error</ToastTitle>
          <ToastBody>Failed to send bulk message: {(error as Error).message}</ToastBody>
        </Toast>,
        { intent: 'error' }
      );
    }
  };

  const getSessionStateBadge = (state: string, isOrphaned?: boolean) => {
    if (isOrphaned) {
      return <Badge color="severe">Orphaned</Badge>;
    }

    const color = state === 'Active' ? 'success' :
                  state === 'Disconnected' ? 'warning' : 'important';
    return <Badge color={color}>{state}</Badge>;
  };

  const handleForceHealthCheck = async () => {
    try {
      await forceHealthCheckMutation.mutateAsync();
      dispatchToast(
        <Toast>
          <ToastTitle>Health Check Complete</ToastTitle>
          <ToastBody>Session health check completed successfully</ToastBody>
        </Toast>,
        { intent: 'success' }
      );
    } catch (error) {
      dispatchToast(
        <Toast>
          <ToastTitle>Error</ToastTitle>
          <ToastBody>Failed to perform health check: {(error as Error).message}</ToastBody>
        </Toast>,
        { intent: 'error' }
      );
    }
  };

  const handleForceRefresh = async () => {
    try {
      await forceRefreshMutation.mutateAsync();
      dispatchToast(
        <Toast>
          <ToastTitle>Refresh Complete</ToastTitle>
          <ToastBody>Cache refresh and health check completed successfully</ToastBody>
        </Toast>,
        { intent: 'success' }
      );
    } catch (error) {
      dispatchToast(
        <Toast>
          <ToastTitle>Error</ToastTitle>
          <ToastBody>Failed to refresh cache: {(error as Error).message}</ToastBody>
        </Toast>,
        { intent: 'error' }
      );
    }
  };

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
        <Spinner label="Loading sessions..." />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <Text>Error loading sessions: {(error as Error).message}</Text>
        <Button onClick={() => refetch()} style={{ marginTop: '1rem' }}>
          Retry
        </Button>
      </div>
    );
  }

  const allSelected = filteredSessions.length > 0 && selectedSessions.size === filteredSessions.length;
  const someSelected = selectedSessions.size > 0;

  return (
    <div style={{ padding: '1rem' }}>
      <Toaster />

      {/* Progress Bar for Refresh Operations */}
      <AdminProgressBar />

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <Text as="h2" size={600}>Admin Session Management</Text>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <Text size={300}>{filteredSessions.length} sessions</Text>
          <Button
            appearance="subtle"
            icon={<CheckmarkCircle20Regular />}
            onClick={handleForceHealthCheck}
            disabled={forceHealthCheckMutation.isPending || isLoading}
          >
            Health Check
          </Button>
          <Button
            appearance="subtle"
            icon={<ArrowClockwise20Regular />}
            onClick={handleForceRefresh}
            disabled={forceRefreshMutation.isPending || isLoading}
          >
            Force Refresh
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap', alignItems: 'end' }}>
        <Field label="Search">
          <SearchBox
            placeholder="Search by user, host pool, or session host..."
            value={searchInput}
            onChange={(_, data) => setSearchInput(data.value)}
            style={{ minWidth: '300px' }}
          />
        </Field>

        <Field label="Host Pool">
          <Dropdown
            placeholder="All host pools"
            value={filters.hostPool || ''}
            onOptionSelect={(_, data) => {
              const newHostPool = data.optionValue === 'all' ? undefined : data.optionValue;

              // If changing host pool, clear session host filter if it's not valid for the new pool
              if (newHostPool !== filters.hostPool) {
                const validHosts = newHostPool
                  ? sessions.filter(s => s.hostPoolName === newHostPool).map(s => s.sessionHostName)
                  : [];

                const shouldClearSessionHost = filters.sessionHost &&
                  (newHostPool === undefined || !validHosts.includes(filters.sessionHost));

                setFilters(prev => ({
                  ...prev,
                  hostPool: newHostPool,
                  sessionHost: shouldClearSessionHost ? undefined : prev.sessionHost
                }));
              } else {
                setFilters(prev => ({ ...prev, hostPool: newHostPool }));
              }
            }}
          >
            <Option value="all">All host pools</Option>
            {uniqueHostPools.map(pool => (
              <Option key={pool} value={pool}>{pool}</Option>
            ))}
          </Dropdown>
        </Field>

        <Field label="Session Host">
          <Dropdown
            placeholder="All session hosts"
            value={filters.sessionHost || ''}
            onOptionSelect={(_, data) =>
              setFilters(prev => ({ ...prev, sessionHost: data.optionValue === 'all' ? undefined : data.optionValue }))
            }
          >
            <Option value="all">All session hosts</Option>
            {uniqueSessionHosts.map(host => (
              <Option key={host} value={host}>{host}</Option>
            ))}
          </Dropdown>
        </Field>

        <Field label="Session State">
          <Dropdown
            placeholder="All states"
            value={filters.sessionState || ''}
            onOptionSelect={(_, data) =>
              setFilters(prev => ({ ...prev, sessionState: data.optionValue === 'all' ? undefined : data.optionValue as SessionState }))
            }
          >
            <Option value="all">All states</Option>
            <Option value="Active">Active</Option>
            <Option value="Disconnected">Disconnected</Option>
            <Option value="Pending">Pending</Option>
            <Option value="LogOff">LogOff</Option>
          </Dropdown>
        </Field>

        <Field label="Show Orphaned Sessions">
          <Checkbox
            checked={filters.orphanedOnly || false}
            onChange={(_, data) =>
              setFilters(prev => ({ ...prev, orphanedOnly: data.checked === true ? true : undefined }))
            }
            label="Show only orphaned sessions"
          />
        </Field>

        <Button
          appearance="subtle"
          icon={<Delete20Regular />}
          onClick={() => {
            setSearchInput('');
            setSearchTerm('');
            setFilters({});
          }}
        >
          Clear Filters
        </Button>
      </div>

      {/* Bulk Actions */}
      {someSelected && (
        <div style={{ marginBottom: '1rem', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <Text>{selectedSessions.size} selected</Text>
          <Button
            appearance="primary"
            icon={<Power20Regular />}
            onClick={() => handleBulkAction('disconnect')}
            disabled={bulkActionMutation.isPending}
          >
            Bulk Disconnect
          </Button>
          <Button
            appearance="primary"
            icon={<SignOut20Regular />}
            onClick={() => handleBulkAction('logoff')}
            disabled={bulkActionMutation.isPending}
          >
            Bulk Log Off
          </Button>
          <Button
            appearance="primary"
            icon={<Chat20Regular />}
            onClick={() => handleBulkAction('message')}
            disabled={bulkActionMutation.isPending}
          >
            Bulk Message
          </Button>
        </div>
      )}

      {/* Sessions Table */}
      <Table>
        <TableHeader>
          <TableRow>
            <TableHeaderCell>
              <Checkbox
                checked={allSelected}
                onChange={(_, data) => handleSelectAll(data.checked === true)}
              />
            </TableHeaderCell>
            <TableHeaderCell>User</TableHeaderCell>
            <TableHeaderCell>Host Pool</TableHeaderCell>
            <TableHeaderCell>Session Host</TableHeaderCell>
            <TableHeaderCell>State</TableHeaderCell>
            <TableHeaderCell>Start Time</TableHeaderCell>
            <TableHeaderCell>Last Activity</TableHeaderCell>
            <TableHeaderCell>Health Check</TableHeaderCell>
            <TableHeaderCell>Actions</TableHeaderCell>
          </TableRow>
        </TableHeader>
        <TableBody>
          {filteredSessions.map((session) => (
            <TableRow key={session.id}>
              <TableCell>
                <Checkbox
                  checked={selectedSessions.has(session.id)}
                  onChange={(_, data) => handleSelectSession(session.id, data.checked === true)}
                />
              </TableCell>
              <TableCell>{session.userPrincipalName}</TableCell>
              <TableCell>{session.hostPoolName}</TableCell>
              <TableCell>{session.sessionHostName}</TableCell>
              <TableCell>{getSessionStateBadge(session.sessionState, session.isOrphaned)}</TableCell>
              <TableCell>{formatDateLocale(session.startTime)}</TableCell>
              <TableCell>{formatDateLocale(session.lastActivityTime)}</TableCell>
              <TableCell>{formatDateLocale(session.lastHealthCheck)}</TableCell>
              <TableCell>
                <div style={{ display: 'flex', gap: '0.25rem' }}>
                  <Button
                    appearance="subtle"
                    icon={<Power20Regular />}
                    size="small"
                    title={session.sessionState !== 'Active' ? 'Only active sessions can be disconnected' : 'Disconnect'}
                    onClick={() => handleSingleAction(session.id, 'disconnect')}
                    disabled={disconnectMutation.isPending || session.sessionState !== 'Active'}
                  />
                  <Button
                    appearance="subtle"
                    icon={<SignOut20Regular />}
                    size="small"
                    title={session.sessionState === 'Active' || session.sessionState === 'Disconnected' || session.sessionState === 'UserDisconnected' ? 'Log Off' : 'Cannot log off session in this state'}
                    onClick={() => handleSingleAction(session.id, 'logoff')}
                    disabled={logOffMutation.isPending || (session.sessionState !== 'Active' && session.sessionState !== 'Disconnected' && session.sessionState !== 'UserDisconnected')}
                  />
                  <Button
                    appearance="subtle"
                    icon={<Chat20Regular />}
                    size="small"
                    title={session.sessionState !== 'Active' ? 'Only active sessions can receive messages' : 'Send Message'}
                    onClick={() => handleSingleAction(session.id, 'message')}
                    disabled={sendMessageMutation.isPending || session.sessionState !== 'Active'}
                  />
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {filteredSessions.length === 0 && (
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <Text>No sessions found matching the current filters.</Text>
        </div>
      )}

      {/* Send Message Dialog */}
      <Dialog open={showMessageDialog} onOpenChange={(_, data) => setShowMessageDialog(data.open)}>
        <DialogSurface>
          <DialogTitle>Send Message to Session</DialogTitle>
          <DialogContent>
            <DialogBody>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <Field label="Message Title" required>
                  <Input
                    value={messageTitle}
                    onChange={(_, data) => setMessageTitle(data.value)}
                    placeholder="Enter message title..."
                  />
                </Field>
                <Field label="Message Body" required>
                  <Textarea
                    value={messageBody}
                    onChange={(_, data) => setMessageBody(data.value)}
                    placeholder="Enter message content..."
                    rows={4}
                  />
                </Field>
              </div>
            </DialogBody>
            <DialogActions>
              <Button
                appearance="secondary"
                onClick={() => {
                  setShowMessageDialog(false);
                  setMessageTitle('');
                  setMessageBody('');
                  setTargetSessionId(null);
                }}
              >
                Cancel
              </Button>
              <Button
                appearance="primary"
                onClick={handleSendMessage}
                disabled={!messageTitle.trim() || !messageBody.trim() || sendMessageMutation.isPending}
              >
                {sendMessageMutation.isPending ? 'Sending...' : 'Send Message'}
              </Button>
            </DialogActions>
          </DialogContent>
        </DialogSurface>
      </Dialog>

      {/* Bulk Send Message Dialog */}
      <Dialog open={showBulkMessageDialog} onOpenChange={(_, data) => setShowBulkMessageDialog(data.open)}>
        <DialogSurface>
          <DialogTitle>Send Message to {selectedSessions.size} Sessions</DialogTitle>
          <DialogContent>
            <DialogBody>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <Field label="Message Title" required>
                  <Input
                    value={messageTitle}
                    onChange={(_, data) => setMessageTitle(data.value)}
                    placeholder="Enter message title..."
                  />
                </Field>
                <Field label="Message Body" required>
                  <Textarea
                    value={messageBody}
                    onChange={(_, data) => setMessageBody(data.value)}
                    placeholder="Enter message content..."
                    rows={4}
                  />
                </Field>
              </div>
            </DialogBody>
            <DialogActions>
              <Button
                appearance="secondary"
                onClick={() => {
                  setShowBulkMessageDialog(false);
                  setMessageTitle('');
                  setMessageBody('');
                }}
              >
                Cancel
              </Button>
              <Button
                appearance="primary"
                onClick={handleBulkSendMessage}
                disabled={!messageTitle.trim() || !messageBody.trim() || bulkActionMutation.isPending}
              >
                {bulkActionMutation.isPending ? 'Sending...' : 'Send to All Selected'}
              </Button>
            </DialogActions>
          </DialogContent>
        </DialogSurface>
      </Dialog>
    </div>
  );
}

/**
 * Memoized version of AdminSessionsTable for performance optimization.
 * Export this component instead of the base component.
 */
export const AdminSessionsTable = memo(AdminSessionsTableComponent);